import { ProvedorDePreferencias } from './contexto-de-preferencias.tsx'
import { ProvedorDeToasts, useToast } from '../componentes/toast.tsx'
/**
 * O shell: sidebar + topbar + área de conteúdo.
 *
 * Estado que vive aqui e em nenhum outro lugar: o modo retraído da sidebar, lembrado
 * no navegador (D-143), os grupos abertos, a entidade ativa e o menu lateral no mobile.
 * Página nenhuma duplica seletor de entidade nem barra de busca (GLOSSARIO).
 *
 * A entidade ativa sai daqui por dois caminhos, e os dois são deliberados: o
 * **contexto React** para as páginas recarregarem quando ela muda, e o **cliente
 * HTTP** para o cabeçalho `X-DV-Entity` viajar em todo pedido sem cada tela
 * lembrar disso (DS-40).
 */
import { useEffect, useRef, useState } from 'react'
import { Outlet, useRouterState } from '@tanstack/react-router'
import { EstadoCarregando } from '../componentes/estados.tsx'
import { Sidebar } from './sidebar.tsx'
import { Topbar } from './topbar.tsx'
import { ContextoDeEntidade, ContextoDoAmbiente } from './contexto-de-entidade.tsx'
import { definirEntidadeDoContexto, ErroDaApi } from '../api/cliente.ts'
import { badgeDeNotificacoes, contextoDeEntidade, meuPerfil, salvarPerfil } from '../api/recursos.ts'
import { EVENTO_DE_NOTIFICACOES } from '../api/evento-notificacoes.ts'
import { EVENTO_DE_PERFIL } from '../api/evento-perfil.ts'
import type { Perfil } from '@dv/contracts'
import { useRecurso } from '../api/use-recurso.ts'
import type { Entidade } from './seletor-entidade.tsx'
import { ProvedorDaBuscaGlobal } from '../componentes/busca-global.tsx'
import { ProvedorDePermissoes } from './contexto-de-permissoes.tsx'
import { DescricaoDeNovaAba } from '../componentes/link-da-entidade.tsx'
import { useTextos } from '../textos.ts'
import { TEXTOS_DO_SHELL } from './shell-textos.ts'
import { grupoDaRota } from './navegacao.ts'
import { ROTULO_POR_CAMINHO } from '../rotas/caminhos.ts'
import { gravarMenuRecolhido, lerMenuRecolhido } from './menu-lateral.ts'

/** Este filho lê o contexto já montado, preservando o estado e a posição do Shell. */
function AtalhoParaConteudo() {
  const texto = useTextos(TEXTOS_DO_SHELL)
  return (
    <a
      href="#conteudo"
      data-teste="pular-para-conteudo"
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-[10px] focus:bg-navy focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
    >
      {texto('pular')}
    </a>
  )
}

/**
 * O seletor vive dentro do provedor de toast para poder informar falha de persistência.
 *
 * As gravações são serializadas. Sem a fila, A → B → C poderia terminar com B salvo
 * caso a resposta de B chegasse por último, e o refresh abriria uma entidade diferente
 * daquela que o usuário acabou de escolher.
 */
function TopbarComEntidadePersistente({
  entidades,
  entidadeAtiva,
  aoTrocarEntidade,
  naoLidas,
  aoAbrirMenuLateral,
  aoAlternarRail,
  rail,
}: {
  readonly entidades: readonly Entidade[]
  readonly entidadeAtiva: Entidade | null
  readonly aoTrocarEntidade: (entidade: Entidade) => void
  readonly naoLidas: number
  readonly aoAbrirMenuLateral: () => void
  readonly aoAlternarRail: () => void
  readonly rail: boolean
}) {
  const toast = useToast()
  const pendente = useRef<Entidade | null>(null)
  const persistindo = useRef(false)
  const ultimaEscolhida = useRef<string | null>(entidadeAtiva?.id ?? null)

  async function persistirPendentes(): Promise<void> {
    if (persistindo.current) return
    persistindo.current = true
    try {
      while (pendente.current !== null) {
        const entidade = pendente.current
        pendente.current = null
        try {
          await salvarPerfil({ preferencias: { entidadePadraoId: entidade.id } })
        } catch (causa) {
          if (ultimaEscolhida.current !== entidade.id) continue
          toast({
            tom: 'erro',
            titulo: 'A entidade mudou, mas a seleção não pôde ser preservada.',
            descricao: causa instanceof ErroDaApi ? causa.message : 'Tente selecionar a entidade novamente.',
          })
        }
      }
    } finally {
      persistindo.current = false
      if (pendente.current !== null) void persistirPendentes()
    }
  }

  function trocar(entidade: Entidade): void {
    if (entidade.id === entidadeAtiva?.id) return
    ultimaEscolhida.current = entidade.id
    pendente.current = entidade

    // Primeiro muda contexto + header. A persistência é durável, mas não segura a UI.
    aoTrocarEntidade(entidade)
    void persistirPendentes()
  }

  return <Topbar
    entidades={entidades}
    entidadeAtiva={entidadeAtiva}
    aoTrocarEntidade={trocar}
    naoLidas={naoLidas}
    aoAbrirMenuLateral={aoAbrirMenuLateral}
    aoAlternarRail={aoAlternarRail}
    rail={rail}
  />
}

/** O rótulo de cada rota, lido pelo pathname cru (sem afirmar ao compilador que todo pathname é uma rota). */
const ROTULO_DA_ROTA: ReadonlyMap<string, string> = new Map(Object.entries(ROTULO_POR_CAMINHO))
/**
 * As telas que nomeiam o REGISTRO aberto na aba (Campanhas, RH, Tarefas) são donas do próprio
 * título; nelas o shell não escreve, para não apagar o nome que a tela acabou de pôr.
 */
const ROTAS_COM_TITULO_PROPRIO: ReadonlySet<string> = new Set(['/campanhas', '/rh', '/tarefas'])

export function Shell() {
  const rotaAtual = useRouterState({ select: (s) => s.location.pathname })
  /*
    D-12: o título da aba segue a rota. Só três telas definiam `document.title`, e nenhuma o
    restaurava: depois de abrir uma campanha, a aba de Pedidos continuava com o nome da campanha.
    O shell escreve o rótulo da rota em toda troca de caminho, menos nas telas que têm título
    próprio (elas o sobrescrevem no instante em que sabem o nome do registro).
  */
  useEffect(() => {
    if (ROTAS_COM_TITULO_PROPRIO.has(rotaAtual)) return
    const rotulo = ROTULO_DA_ROTA.get(rotaAtual)
    document.title = rotulo === undefined ? 'DigitalView' : `${rotulo} · DigitalView`
  }, [rotaAtual])
  const grupoInicial = grupoDaRota(rotaAtual)
  /*
    A barra nasce como o navegador lembrou (D-143, DS-549). A leitura é síncrona, no
    inicializador, para a primeira pintura já sair certa, sem a barra piscar aberta.
  */
  const [rail, setRail] = useState(() => lerMenuRecolhido(() => window.localStorage))
  const [menuAberto, setMenuAberto] = useState(false)
  const [gruposAbertos, setGruposAbertos] = useState<ReadonlySet<string>>(
    () => new Set(grupoInicial === null ? [] : [grupoInicial]),
  )
  const [entidades, setEntidades] = useState<readonly Entidade[]>([])
  const [contextoCarregando, setContextoCarregando] = useState(true)
  /**
   * Quem está na sessão, para o rodapé da sidebar.
   *
   * Um pedido só, aqui em cima: se cada tela perguntasse "quem sou eu?", o nome
   * apareceria e sumiria a cada navegação. A GUARDA de sessão não mora aqui — mora
   * no `beforeLoad` da rota (`rotas/raiz.tsx`), porque decisão de deixar entrar tem
   * de acontecer ANTES de o shell montar, não durante.
   */
  const perfil = useRecurso(() => meuPerfil(), [])
  const badge = useRecurso((sinal) => badgeDeNotificacoes(sinal), [])
  const [entidadeAtiva, setEntidadeAtiva] = useState<Entidade | null>(null)

  useEffect(() => {
    const grupo = grupoDaRota(rotaAtual)
    setGruposAbertos(new Set(grupo === null ? [] : [grupo]))
  }, [rotaAtual])

  useEffect(() => {
    const atualizar = (evento: Event): void => {
      if (evento instanceof CustomEvent && typeof evento.detail === 'number') {
        badge.definir({ naoLidas: evento.detail })
      } else {
        badge.recarregar()
      }
    }
    window.addEventListener(EVENTO_DE_NOTIFICACOES, atualizar)
    return () => {
      window.removeEventListener(EVENTO_DE_NOTIFICACOES, atualizar)
    }
  }, [badge.definir, badge.recarregar])

  // O Perfil salvou nome ou foto: o rodapé da barra lateral troca na hora (PK-04).
  useEffect(() => {
    const trocar = (evento: Event): void => {
      if (evento instanceof CustomEvent) perfil.definir(evento.detail as Perfil)
    }
    window.addEventListener(EVENTO_DE_PERFIL, trocar)
    return () => {
      window.removeEventListener(EVENTO_DE_PERFIL, trocar)
    }
  }, [perfil.definir])

  /**
   * O seletor lista as entidades ATIVAS e, quando existe grupo configurado, a visão
   * consolidada (DS-09/DS-40). Nada é inventado: sem grupo, não há "Consolidado".
   */
  useEffect(() => {
    if (perfil.carregando) return
    setContextoCarregando(true)
    let vivo = true
    void contextoDeEntidade()
      .then((contexto) => {
        if (!vivo) return
        const lista: Entidade[] = [
          ...contexto.entidades.map((e) => ({
            id: e.id,
            nome: e.nome,
            meta: e.moedaFuncional,
            ambiente: e.ambiente,
          })),
          ...contexto.grupos.map((g) => ({
            id: g.id,
            nome: g.nome,
            meta: g.moedaReporte,
            ambiente: 'consolidado' as const,
          })),
        ]
        setEntidades(lista)
        const primeira = lista.find((e) => e.id === perfil.dado?.preferencias.entidadePadraoId) ?? lista[0]
        if (primeira !== undefined) {
          setEntidadeAtiva(primeira)
          definirEntidadeDoContexto(primeira.id)
        }
      })
      .catch(() => {
        // Sem contexto, o cabeçalho fica sem seletor — e cada página mostra o
        // próprio estado de erro, que é onde a pessoa entende o que houve.
      })
      .finally(() => { if (vivo) setContextoCarregando(false) })
    return () => {
      vivo = false
    }
  }, [perfil.carregando, perfil.dado?.preferencias.entidadePadraoId])

  /**
   * Fixa a barra aberta ou recolhida, e grava a escolha. Só o GESTO grava (o botão do
   * cabeçalho, o do topo da barra que espia, o toque num grupo): quem nunca alternou a barra
   * não deixa rastro no navegador.
   */
  function fixarRail(recolhido: boolean): void {
    setRail(recolhido)
    gravarMenuRecolhido(() => window.localStorage, recolhido)
  }

  function trocarEntidade(entidade: Entidade): void {
    setEntidadeAtiva(entidade)
    definirEntidadeDoContexto(entidade.id)
  }

  return (
    <ContextoDeEntidade.Provider value={entidadeAtiva?.id ?? null}>
      <ContextoDoAmbiente.Provider value={entidadeAtiva?.ambiente ?? null}>
      <ProvedorDePreferencias preferencias={perfil.dado?.preferencias}>
      {/* A descrição "abre em nova aba", UMA vez, para todo `LinkDaEntidade` apontar por
          `aria-describedby` (D-136, DS-533). Dentro das preferências porque segue o idioma. */}
      <DescricaoDeNovaAba />
      {/*
        As permissões POR FORA da pilha de toasts, e a ordem é o ponto (D-136, DS-533).

        O conteúdo de um aviso é renderizado onde o provedor está, não onde foi criado (ver
        `raiz.tsx`). Com os toasts por fora, um `LinkDaEntidade` dentro de um aviso lia o
        nível VAZIO, e o nome da tarefa recém-criada virava texto para todo mundo, sem erro
        nenhum. As permissões não dependem de toast, então descem um degrau sem custo; os
        toasts continuam dentro das preferências, que era o motivo de terem descido.
      */}
      <ProvedorDePermissoes perfil={perfil.dado}>
      <ProvedorDeToasts>
      <ProvedorDaBuscaGlobal usuarioId={perfil.dado?.id ?? 'sessao-pendente'}>
      <div className="min-h-screen flex">
        {/*
          O primeiro `Tab` da página pula o menu (lote U3).

          A barra lateral tem vinte e poucos links e vem ANTES do conteúdo no DOM — que é
          a ordem certa para a leitura, e a errada para quem chega pelo teclado querendo o
          conteúdo. Sem este atalho, ler a segunda tabela de uma tela custa vinte e três
          `Tab`, TODA VEZ. Ele é invisível até receber foco, e aí aparece por cima de tudo.
        */}
        <AtalhoParaConteudo />
        <Sidebar
          rail={rail}
          aberta={menuAberto}
          aoFixarAberta={() => {
            fixarRail(false)
          }}
          gruposAbertos={gruposAbertos}
          usuario={
            perfil.dado === null
              ? null
              : { nome: perfil.dado.nome, nivel: perfil.dado.nivelAcessoNome, avatarAnexoId: perfil.dado.avatarAnexoId }
          }
          aoAlternarGrupo={(chave) => {
            setGruposAbertos((atual) => {
              const proximo = new Set(atual)
              if (proximo.has(chave)) proximo.delete(chave)
              else proximo.add(chave)
              return proximo
            })
          }}
        />

        {menuAberto && (
          <div
            className="fixed inset-0 z-30 bg-[var(--dv-veu)] md:hidden"
            data-teste="veu-menu-movel"
            onClick={() => {
              setMenuAberto(false)
            }}
            aria-hidden="true"
          />
        )}

        <div
          className={
            'flex-1 min-w-0 flex flex-col transition-[margin] duration-200 ' + (rail ? 'md:ml-19' : 'md:ml-64')
          }
        >
          <TopbarComEntidadePersistente
            entidades={entidades}
            entidadeAtiva={entidadeAtiva}
            aoTrocarEntidade={trocarEntidade}
            naoLidas={badge.dado?.naoLidas ?? 0}
            aoAbrirMenuLateral={() => {
              setMenuAberto(true)
            }}
            aoAlternarRail={() => {
              fixarRail(!rail)
            }}
            rail={rail}
          />
          {/* A marcação do Sandbox vive SÓ no seletor do topo (âmbar + frasco), por
              decisão do fundador em 31/08. A faixa que ficava aqui saiu: ela repetia,
              em duas linhas de tela por página, o que o botão já diz o tempo todo. */}
          {/* `tabIndex={-1}`: sem ele o foco não PARA aqui ao seguir o atalho — o
              navegador rola até a âncora e o próximo `Tab` recomeça do topo. */}
          <main id="conteudo" tabIndex={-1} className="flex-1 p-4 md:p-6 lg:p-8">
            {/* A primeira leitura já leva a entidade escolhida. Montar antes faria
                a API usar o padrão e recarregar depois, perdendo edições iniciadas. */}
            {contextoCarregando ? <EstadoCarregando /> : <Outlet key={entidadeAtiva?.id ?? 'sem-entidade'} />}
          </main>
        </div>
      </div>
      </ProvedorDaBuscaGlobal>
      </ProvedorDeToasts>
      </ProvedorDePermissoes>
      </ProvedorDePreferencias>
      </ContextoDoAmbiente.Provider>
    </ContextoDeEntidade.Provider>
  )
}
